import React, { useState } from 'react';
import { ChevronDown, ChevronRight, MoreHorizontal, FileText, Package, SkipForward, History, AlertTriangle, Users, Factory, Settings, Calendar, MapPin, Activity, Target, TrendingUp, Plus, Save, X, Clock, CheckCircle, BarChart3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';

interface WorkOrderItem {
  id: string;
  woNo: string;
  type: string;
  resourceType: string;
  position: string;
  productionType: string;
  itemType: string;
  targetDate: string;
  itemCode: string;
  itemName: string;
  plannedQty: number;
  issuedQty: number;
  completedQty: number;
  uom: string;
  warehouse: string;
  drawingNum: string;
  baseQty: number;
  woStatus: string;
  level: number;
  hasChildren: boolean;
  isExpanded?: boolean;
  children?: WorkOrderItem[];
}

const mockData: WorkOrderItem[] = [
  {
    id: '1',
    woNo: '59',
    type: 'MAKE',
    resourceType: 'Machine Center',
    position: '10',
    productionType: 'Manufacturing',
    itemType: 'In House',
    targetDate: '25-10-2023',
    itemCode: 'FGPHA30010',
    itemName: '120 Shock Absorber Assembly',
    plannedQty: 1000,
    issuedQty: 850,
    completedQty: 750,
    uom: 'NOS',
    warehouse: 'WH1001',
    drawingNum: 'DRG-SHA-001',
    baseQty: 1,
    woStatus: 'In Progress',
    level: 0,
    hasChildren: true,
    isExpanded: true,
    children: [
      {
        id: '1-1',
        woNo: '59.1',
        type: 'MAKE',
        resourceType: 'Work Center',
        position: '10',
        productionType: 'Machining',
        itemType: 'In House',
        targetDate: '22-10-2023',
        itemCode: 'SUBPHA30011',
        itemName: 'Shock Absorber Body',
        plannedQty: 1000,
        issuedQty: 900,
        completedQty: 850,
        uom: 'NOS',
        warehouse: 'WH1001',
        drawingNum: 'DRG-SHA-002',
        baseQty: 1,
        woStatus: 'In Progress',
        level: 1,
        hasChildren: false,
      },
      {
        id: '1-2',
        woNo: '59.2',
        type: 'Sub Contract',
        resourceType: 'Vendor',
        position: '20',
        productionType: 'Sub Contracting',
        itemType: 'Sub Contract',
        targetDate: '23-10-2023',
        itemCode: 'SUBPHA30012',
        itemName: 'Shock Absorber Spring',
        plannedQty: 1000,
        issuedQty: 1000,
        completedQty: 900,
        uom: 'NOS',
        warehouse: 'WH1001',
        drawingNum: 'DRG-SHA-003',
        baseQty: 1,
        woStatus: 'In Progress',
        level: 1,
        hasChildren: false,
      },
      {
        id: '1-3',
        woNo: '59.3',
        type: 'BUY',
        resourceType: 'Procurement',
        position: '30',
        productionType: 'Purchase',
        itemType: 'Purchase',
        targetDate: '20-10-2023',
        itemCode: 'RMPHA30013',
        itemName: 'Rubber Seal',
        plannedQty: 2000,
        issuedQty: 1800,
        completedQty: 1500,
        uom: 'NOS',
        warehouse: 'WH1001',
        drawingNum: '',
        baseQty: 2,
        woStatus: 'Completed',
        level: 1,
        hasChildren: false,
      },
    ]
  },
  {
    id: '2',
    woNo: '60',
    type: 'MAKE',
    resourceType: 'Machine Center',
    position: '10',
    productionType: 'Manufacturing',
    itemType: 'In House',
    targetDate: '28-10-2023',
    itemCode: 'FGPHA40001',
    itemName: 'Brake Assembly',
    plannedQty: 500,
    issuedQty: 300,
    completedQty: 200,
    uom: 'NOS',
    warehouse: 'WH1002',
    drawingNum: 'DRG-BRK-001',
    baseQty: 1,
    woStatus: 'Planned',
    level: 0,
    hasChildren: true,
    isExpanded: false,
    children: [
      {
        id: '2-1',
        woNo: '60.1',
        type: 'MAKE',
        resourceType: 'Work Center',
        position: '10',
        productionType: 'Machining',
        itemType: 'In House',
        targetDate: '26-10-2023',
        itemCode: 'SUBBRK40001',
        itemName: 'Brake Disc',
        plannedQty: 500,
        issuedQty: 250,
        completedQty: 200,
        uom: 'NOS',
        warehouse: 'WH1002',
        drawingNum: 'DRG-BRK-002',
        baseQty: 1,
        woStatus: 'Planned',
        level: 1,
        hasChildren: false,
      },
      {
        id: '2-2',
        woNo: '60.2',
        type: 'BUY',
        resourceType: 'Procurement',
        position: '20',
        productionType: 'Purchase',
        itemType: 'Purchase',
        targetDate: '24-10-2023',
        itemCode: 'RMBRK40002',
        itemName: 'Brake Pad Material',
        plannedQty: 1000,
        issuedQty: 500,
        completedQty: 400,
        uom: 'KGS',
        warehouse: 'WH1002',
        drawingNum: '',
        baseQty: 2,
        woStatus: 'Planned',
        level: 1,
        hasChildren: false,
      },
    ]
  }
];

const actionMenuOptions = [
  { label: 'Report Completion', icon: FileText, color: 'text-green-600' },
  { label: 'Material Request', icon: Package, color: 'text-blue-600' },
  { label: 'Skip Sub Production', icon: SkipForward, color: 'text-orange-600' },
  { label: 'Transaction History', icon: History, color: 'text-purple-600' },
  { label: 'Rejection', icon: AlertTriangle, color: 'text-red-600' },
  { label: 'Sub Contracting', icon: Users, color: 'text-indigo-600' },
];

export function WorkOrderList() {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(['1']));
  const [selectedItem, setSelectedItem] = useState<WorkOrderItem | null>(null);
  const [isActionsDialogOpen, setIsActionsDialogOpen] = useState(false);
  const [isNewWorkOrderDialogOpen, setIsNewWorkOrderDialogOpen] = useState(false);
  const [newWorkOrder, setNewWorkOrder] = useState({
    woNo: '',
    type: '',
    resourceType: '',
    position: '',
    productionType: '',
    itemType: '',
    targetDate: '',
    itemCode: '',
    itemName: '',
    plannedQty: '',
    uom: '',
    warehouse: '',
    drawingNum: '',
    baseQty: '',
    woStatus: 'Planned'
  });

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const openActionsDialog = (item: WorkOrderItem) => {
    setSelectedItem(item);
    setIsActionsDialogOpen(true);
  };

  const handleActionClick = (action: string) => {
    console.log(`Action: ${action} for WO: ${selectedItem?.woNo}`);
    setIsActionsDialogOpen(false);
  };

  const handleNewWorkOrderSubmit = () => {
    console.log('Creating new work order:', newWorkOrder);
    setIsNewWorkOrderDialogOpen(false);
    // Reset form
    setNewWorkOrder({
      woNo: '',
      type: '',
      resourceType: '',
      position: '',
      productionType: '',
      itemType: '',
      targetDate: '',
      itemCode: '',
      itemName: '',
      plannedQty: '',
      uom: '',
      warehouse: '',
      drawingNum: '',
      baseQty: '',
      woStatus: 'Planned'
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setNewWorkOrder(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getProgressPercentage = (completed: number, planned: number) => {
    if (planned === 0) return 0;
    return Math.round((completed / planned) * 100);
  };



  // const getStatusIcon = (status: string) => {
  //   switch (status.toLowerCase()) {
  //     case 'completed':
  //       return CheckCircle;
  //     case 'in progress':
  //       return Zap;
  //     case 'planned':
  //       return Clock;
  //     default:
  //       return Target;
  //   }
  // };

  const renderRows = (items: WorkOrderItem[]): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];

    items.forEach((item) => {
      const isExpanded = expandedRows.has(item.id);
      
      const progressPercentage = getProgressPercentage(item.completedQty, item.plannedQty);
      
      rows.push(
        <TableRow key={item.id} className="hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-indigo-50/30 border-b border-gray-100 transition-all duration-200">
          {/* WO NO */}
          <TableCell className="py-5">
            <div className="flex items-center" style={{ paddingLeft: `${item.level * 24}px` }}>
              {item.hasChildren ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-8 h-8 p-0 mr-3 hover:bg-blue-100 hover:scale-110 transition-all duration-200 rounded-lg"
                  onClick={() => toggleRow(item.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-blue-600" />
                  )}
                </Button>
              ) : (
                <div className="w-11" />
              )}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-gray-900 text-lg">{item.woNo}</span>
                  {item.level === 0 && (
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <div className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        Main
                      </div>
                    </div>
                  )}
                </div>
                {item.level === 0 && (
                  <div className="flex flex-col">
                    <div className="w-20 bg-gray-200 rounded-full h-1.5 mb-1">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-500 ${
                          progressPercentage >= 75 ? 'bg-green-500' :
                          progressPercentage >= 50 ? 'bg-blue-500' :
                          progressPercentage >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 font-medium">{progressPercentage}%</span>
                  </div>
                )}
              </div>
            </div>
          </TableCell>
          
          {/* Type */}
          <TableCell className="py-5">
            <Badge variant="outline" className={`font-semibold px-3 py-1.5 shadow-sm border-2 transition-all duration-200 hover:scale-105 ${
              item.type === 'MAKE' ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-green-300 hover:border-green-400' :
              item.type === 'BUY' ? 'bg-gradient-to-r from-blue-50 to-sky-50 text-blue-700 border-blue-300 hover:border-blue-400' :
              item.type === 'Sub Contract' ? 'bg-gradient-to-r from-purple-50 to-violet-50 text-purple-700 border-purple-300 hover:border-purple-400' :
              'bg-gradient-to-r from-gray-50 to-slate-50 text-gray-700 border-gray-300 hover:border-gray-400'
            }`}>
              {item.type}
            </Badge>
          </TableCell>
          
          {/* Resource Type */}
          <TableCell className="py-5">
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors">
              <div className="p-1 bg-blue-100 rounded">
                <Factory className="w-3 h-3 text-blue-600" />
              </div>
              <span className="text-sm text-gray-700 font-medium">{item.resourceType}</span>
            </div>
          </TableCell>
          
          {/* Position */}
          <TableCell className="py-5">
            <div className="flex items-center space-x-2 bg-orange-50 rounded-lg px-3 py-2 hover:bg-orange-100 transition-colors">
              <div className="p-1 bg-orange-100 rounded">
                <MapPin className="w-3 h-3 text-orange-600" />
              </div>
              <span className="text-sm text-gray-700 font-medium">{item.position}</span>
            </div>
          </TableCell>
          
          {/* Production Type */}
          <TableCell className="py-5">
            <div className="flex items-center space-x-2 bg-purple-50 rounded-lg px-3 py-2 hover:bg-purple-100 transition-colors">
              <div className="p-1 bg-purple-100 rounded">
                <Settings className="w-3 h-3 text-purple-600" />
              </div>
              <span className="text-sm text-gray-700 font-medium">{item.productionType}</span>
            </div>
          </TableCell>
          
          {/* Item Type */}
          <TableCell className="py-5">
            <span className="text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-2 rounded-lg font-medium shadow-sm">
              {item.itemType}
            </span>
          </TableCell>
          
          {/* Target Date */}
          <TableCell className="py-5">
            <div className="flex items-center space-x-2 bg-green-50 rounded-lg px-3 py-2 hover:bg-green-100 transition-colors">
              <div className="p-1 bg-green-100 rounded">
                <Calendar className="w-3 h-3 text-green-600" />
              </div>
              <span className="text-sm text-gray-700 font-medium">{item.targetDate}</span>
            </div>
          </TableCell>
          
          {/* Item Code */}
          <TableCell className="py-5">
            <code className="px-3 py-2 bg-gradient-to-r from-slate-100 to-slate-200 rounded-lg text-xs font-mono text-slate-800 border border-slate-300 shadow-sm hover:shadow-md transition-shadow">
              {item.itemCode}
            </code>
          </TableCell>
          
          {/* Item Name */}
          <TableCell className="py-5">
            <div className="max-w-48">
              <div className="text-sm text-gray-900 font-semibold truncate" title={item.itemName}>
                {item.itemName}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                ID: {item.itemCode.slice(-6)}
              </div>
            </div>
          </TableCell>
          
          {/* Planned Qty */}
          <TableCell className="py-5 text-right">
            <div className="bg-blue-50 rounded-lg px-3 py-2 hover:bg-blue-100 transition-colors inline-flex items-center space-x-2">
              <div className="p-1 bg-blue-100 rounded">
                <Target className="w-3 h-3 text-blue-600" />
              </div>
              <span className="font-bold text-gray-900 text-sm">
                {item.plannedQty.toLocaleString()}
              </span>
            </div>
          </TableCell>
          
          {/* Issued Qty */}
          <TableCell className="py-5 text-right">
            <div className="bg-orange-50 rounded-lg px-3 py-2 hover:bg-orange-100 transition-colors inline-flex items-center space-x-2">
              <div className="p-1 bg-orange-100 rounded">
                <Activity className="w-3 h-3 text-orange-600" />
              </div>
              <span className="font-semibold text-gray-700 text-sm">
                {item.issuedQty.toLocaleString()}
              </span>
            </div>
          </TableCell>
          
          {/* Completed Qty */}
          <TableCell className="py-5 text-right">
            <div className="bg-green-50 rounded-lg px-3 py-2 hover:bg-green-100 transition-colors inline-flex items-center space-x-2">
              <div className="p-1 bg-green-100 rounded">
                <TrendingUp className="w-3 h-3 text-green-600" />
              </div>
              <span className="font-semibold text-gray-700 text-sm">
                {item.completedQty.toLocaleString()}
              </span>
            </div>
          </TableCell>
          
          {/* UOM */}
          <TableCell className="py-5">
            <span className="text-sm text-blue-700 bg-gradient-to-r from-blue-50 to-blue-100 px-3 py-2 rounded-lg font-semibold border border-blue-200 shadow-sm">
              {item.uom}
            </span>
          </TableCell>
          
          {/* Warehouse */}
          <TableCell className="py-5">
            <span className="text-sm text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-2 rounded-lg font-semibold border border-gray-300 shadow-sm">
              {item.warehouse}
            </span>
          </TableCell>
          
          {/* Drawing Num */}
          <TableCell className="py-5">
            {item.drawingNum ? (
              <code className="px-3 py-2 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg text-xs font-mono text-indigo-700 border border-indigo-200 shadow-sm hover:shadow-md transition-shadow">
                {item.drawingNum}
              </code>
            ) : (
              <span className="text-gray-400 text-xs bg-gray-50 px-3 py-2 rounded-lg">—</span>
            )}
          </TableCell>
          
          {/* Base Qty */}
          <TableCell className="py-5 text-right">
            <span className="text-sm text-gray-700 font-bold bg-gray-100 px-3 py-2 rounded-lg">
              {item.baseQty}
            </span>
          </TableCell>
          
          {/* WO Status - Actions Arrow */}
          <TableCell className="py-5">
            <div className="flex items-center justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="w-10 h-10 p-0 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300 rounded-xl hover:scale-110 transition-all duration-200 shadow-sm hover:shadow-md"
                onClick={() => openActionsDialog(item)}
              >
                <MoreHorizontal className="w-4 h-4 text-gray-600" />
              </Button>
            </div>
          </TableCell>
        </TableRow>
      );

      if (isExpanded && item.children) {
        rows.push(...renderRows(item.children));
      }
    });

    return rows;
  };

  return (
    <div className="w-full max-w-full mx-auto p-6 bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg border border-gray-200/50">
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Factory className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Work Order Management
                </h1>
                <p className="text-gray-600 text-lg mt-1">
                  Advanced production tracking with intelligent workflow management
                </p>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-semibold">System Online</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600 font-semibold">Real-time Analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-right space-y-3">
            <div className="flex items-center space-x-3">
              <Badge variant="outline" className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-700 px-4 py-2 font-semibold shadow-sm">
                <Factory className="w-4 h-4 mr-2" />
                {mockData.reduce((total, item) => total + (item.children?.length || 0) + 1, 0)} Active Orders
              </Badge>
              <Badge variant="outline" className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700 px-4 py-2 font-semibold shadow-sm">
                <CheckCircle className="w-4 h-4 mr-2" />
                85% Efficiency
              </Badge>
            </div>
            <div className="text-sm text-gray-500">
              Live Production Dashboard • Updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
        {/* Subheader */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-2xl">Open Work Order List</h2>
              <p className="text-blue-100 text-base mt-1">
                Real-time production monitoring with intelligent resource allocation
              </p>
              <div className="flex items-center space-x-6 text-blue-100 mt-3">
                <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">Live Status</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Updated: {new Date().toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 rounded-2xl p-4">
                <Factory className="w-8 h-8 text-white" />
              </div>
              <div className="text-right text-white">
                <div className="text-base font-medium">Manufacturing</div>
                <div className="text-xl font-bold">Dashboard</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Table */}
        <div className="overflow-x-auto bg-gradient-to-b from-white to-gray-50/50">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-gray-50 to-blue-50/50 border-b-2 border-gray-200">
                <TableHead className="font-bold text-gray-800 py-4 text-sm">WO NO</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Type</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Resource Type</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Position</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Production Type</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Item Type</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Target Date</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Item Code</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Item Name</TableHead>
                <TableHead className="font-bold text-gray-800 text-right text-sm">Planned Qty</TableHead>
                <TableHead className="font-bold text-gray-800 text-right text-sm">Issued Qty</TableHead>
                <TableHead className="font-bold text-gray-800 text-right text-sm">Completed Qty</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">UOM</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Warehouse</TableHead>
                <TableHead className="font-bold text-gray-800 text-sm">Drawing Num</TableHead>
                <TableHead className="font-bold text-gray-800 text-right text-sm">Base Qty</TableHead>
                <TableHead className="font-bold text-gray-800 text-center text-sm">WO Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderRows(mockData)}
            </TableBody>
          </Table>
        </div>
        
        {/* Footer */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 px-8 py-6 border-t-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 rounded-xl"
                onClick={() => setIsNewWorkOrderDialogOpen(true)}
              >
                <Plus className="w-5 h-5 mr-2" />
                New Work Order
              </Button>
              <Button variant="outline" className="px-6 py-3 border-2 border-gray-300 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 rounded-xl font-semibold">
                ADD
              </Button>
              <Button variant="outline" className="px-6 py-3 border-2 border-gray-300 hover:border-red-300 hover:bg-red-50 transition-all duration-200 rounded-xl font-semibold">
                CANCEL
              </Button>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-2 bg-green-100 rounded-lg px-3 py-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-700 font-semibold">Active Production</span>
              </div>
              <div className="flex items-center space-x-2 bg-blue-100 rounded-lg px-3 py-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-blue-700 font-semibold">System Online • {new Date().toLocaleTimeString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Dialog */}
      <Dialog open={isActionsDialogOpen} onOpenChange={setIsActionsDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl shadow-2xl border-0">
          <DialogHeader className="space-y-4 pb-6">
            <DialogTitle className="flex items-center space-x-3 text-xl">
              <div className="p-3 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <span className="font-bold text-gray-900">Work Order Actions</span>
            </DialogTitle>
            <DialogDescription>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <div className="font-bold text-gray-900">Work Order: <span className="text-blue-600">{selectedItem?.woNo}</span></div>
                <div className="text-sm text-gray-600 mt-1 font-medium">{selectedItem?.itemName}</div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-gray-500">
                    Progress: <span className="font-bold">{getProgressPercentage(selectedItem?.completedQty || 0, selectedItem?.plannedQty || 0)}%</span> • Type: <span className="font-bold">{selectedItem?.type}</span>
                  </div>
                  <Progress 
                    value={getProgressPercentage(selectedItem?.completedQty || 0, selectedItem?.plannedQty || 0)} 
                    className="w-20 h-2"
                  />
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            {actionMenuOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <Card key={option.label} className="cursor-pointer hover:shadow-lg transition-all duration-200 border-0 shadow-md hover:scale-102 bg-gradient-to-r from-white to-gray-50/50">
                  <CardContent 
                    className="flex items-center space-x-4 p-4 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/30 rounded-xl transition-all duration-200"
                    onClick={() => handleActionClick(option.label)}
                  >
                    <div className={`p-3 rounded-xl shadow-sm ${
                      option.color === 'text-green-600' ? 'bg-gradient-to-r from-green-100 to-emerald-100' :
                      option.color === 'text-blue-600' ? 'bg-gradient-to-r from-blue-100 to-sky-100' :
                      option.color === 'text-orange-600' ? 'bg-gradient-to-r from-orange-100 to-amber-100' :
                      option.color === 'text-purple-600' ? 'bg-gradient-to-r from-purple-100 to-violet-100' :
                      option.color === 'text-red-600' ? 'bg-gradient-to-r from-red-100 to-rose-100' :
                      'bg-gradient-to-r from-indigo-100 to-blue-100'
                    }`}>
                      <IconComponent className={`w-5 h-5 ${option.color}`} />
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900">{option.label}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 transition-colors" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Work Order Dialog */}
      <Dialog open={isNewWorkOrderDialogOpen} onOpenChange={setIsNewWorkOrderDialogOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border-0">
          <DialogHeader className="space-y-4 pb-6 border-b border-gray-100">
            <DialogTitle className="flex items-center space-x-3 text-2xl">
              <div className="p-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl shadow-sm">
                <Plus className="w-7 h-7 text-green-600" />
              </div>
              <span className="font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Create New Work Order
              </span>
            </DialogTitle>
            <DialogDescription className="text-base text-gray-600">
              Define a new work order for production planning and resource allocation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-8 py-6">
            {/* Basic Information */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <Factory className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Basic Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="woNo" className="text-sm font-semibold text-gray-700">Work Order No.</Label>
                  <Input
                    id="woNo"
                    placeholder="Enter WO number"
                    value={newWorkOrder.woNo}
                    onChange={(e) => handleInputChange('woNo', e.target.value)}
                    className="bg-gradient-to-r from-gray-50 to-gray-50/50 border-gray-300 focus:border-blue-400 focus:ring-blue-200 rounded-xl h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={newWorkOrder.type} onValueChange={(value) => handleInputChange('type', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MAKE">MAKE</SelectItem>
                      <SelectItem value="BUY">BUY</SelectItem>
                      <SelectItem value="Sub Contract">Sub Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="resourceType">Resource Type</Label>
                  <Select value={newWorkOrder.resourceType} onValueChange={(value) => handleInputChange('resourceType', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select resource" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Machine Center">Machine Center</SelectItem>
                      <SelectItem value="Work Center">Work Center</SelectItem>
                      <SelectItem value="Vendor">Vendor</SelectItem>
                      <SelectItem value="Procurement">Procurement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Production Details */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Settings className="w-4 h-4 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Production Details</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    placeholder="e.g., 10"
                    value={newWorkOrder.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="productionType">Production Type</Label>
                  <Select value={newWorkOrder.productionType} onValueChange={(value) => handleInputChange('productionType', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select production type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="Machining">Machining</SelectItem>
                      <SelectItem value="Purchase">Purchase</SelectItem>
                      <SelectItem value="Sub Contracting">Sub Contracting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="itemType">Item Type</Label>
                  <Select value={newWorkOrder.itemType} onValueChange={(value) => handleInputChange('itemType', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select item type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="In House">In House</SelectItem>
                      <SelectItem value="Purchase">Purchase</SelectItem>
                      <SelectItem value="Sub Contract">Sub Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="targetDate">Target Date</Label>
                  <Input
                    id="targetDate"
                    type="date"
                    value={newWorkOrder.targetDate}
                    onChange={(e) => handleInputChange('targetDate', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Item Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4 text-orange-600" />
                <h3 className="font-semibold text-gray-900">Item Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="itemCode">Item Code</Label>
                  <Input
                    id="itemCode"
                    placeholder="Enter item code"
                    value={newWorkOrder.itemCode}
                    onChange={(e) => handleInputChange('itemCode', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="itemName">Item Name</Label>
                  <Input
                    id="itemName"
                    placeholder="Enter item name"
                    value={newWorkOrder.itemName}
                    onChange={(e) => handleInputChange('itemName', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Quantity & Storage */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-green-600" />
                <h3 className="font-semibold text-gray-900">Quantity & Storage</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plannedQty">Planned Qty</Label>
                  <Input
                    id="plannedQty"
                    type="number"
                    placeholder="0"
                    value={newWorkOrder.plannedQty}
                    onChange={(e) => handleInputChange('plannedQty', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="uom">UOM</Label>
                  <Select value={newWorkOrder.uom} onValueChange={(value) => handleInputChange('uom', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select UOM" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NOS">NOS</SelectItem>
                      <SelectItem value="KGS">KGS</SelectItem>
                      <SelectItem value="LTR">LTR</SelectItem>
                      <SelectItem value="MTR">MTR</SelectItem>
                      <SelectItem value="PCS">PCS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="warehouse">Warehouse</Label>
                  <Select value={newWorkOrder.warehouse} onValueChange={(value) => handleInputChange('warehouse', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WH1001">WH1001</SelectItem>
                      <SelectItem value="WH1002">WH1002</SelectItem>
                      <SelectItem value="WH1003">WH1003</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="drawingNum">Drawing Number</Label>
                  <Input
                    id="drawingNum"
                    placeholder="Drawing number"
                    value={newWorkOrder.drawingNum}
                    onChange={(e) => handleInputChange('drawingNum', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="baseQty">Base Qty</Label>
                  <Input
                    id="baseQty"
                    type="number"
                    placeholder="1"
                    value={newWorkOrder.baseQty}
                    onChange={(e) => handleInputChange('baseQty', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-500 font-medium">
              Complete all required fields to create your work order
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => setIsNewWorkOrderDialogOpen(false)}
                className="px-6 py-3 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 rounded-xl font-semibold transition-all duration-200"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleNewWorkOrderSubmit}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              >
                <Save className="w-4 h-4 mr-2" />
                Create Work Order
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}